/**
 * Повтор волны по журналу: то, что делает сервер (S2) с присланным `WaveLog`, и что клиент может сделать для
 * самопроверки. Один сид, один баланс, одни команды → один {@link WaveResult} (вместе со `stateHash`).
 */
import type {
  AttackDefense,
  AttackLog,
  BalanceData,
  BattleEvent,
  RunState,
  TimedCommand,
  WaveLog,
  WaveResult,
} from "@newfungame/contracts";
import { createBattle, MAX_WAVE_TICKS, type BattleHooks } from "./battle";

export interface ReplayInput {
  balance: BalanceData;
  run: RunState;
  seed: number;
  commands: readonly TimedCommand[];
  hooks?: BattleHooks;
  /** См. `BattleInit.autoSkillsAllowed`. */
  autoSkillsAllowed?: boolean;
  /** Бой атаки участка (режим «Карта»): состав защиты. */
  attack?: AttackDefense;
  /** События каждого тика: замеры симулятора (`sim-bot`), на итог не влияет. */
  onEvents?: (events: readonly BattleEvent[]) => void;
}

/** Прогнать волну целиком без рендера и вернуть итог. */
export function replayWave(input: ReplayInput): WaveResult {
  const battle = createBattle({
    balance: input.balance,
    run: input.run,
    seed: input.seed,
    hooks: input.hooks,
    ...(input.attack ? { attack: input.attack } : {}),
    ...(input.autoSkillsAllowed !== undefined ? { autoSkillsAllowed: input.autoSkillsAllowed } : {}),
  });
  for (const c of input.commands) battle.schedule(c);
  while (!battle.isOver() && battle.tick() < MAX_WAVE_TICKS) {
    const events = battle.step();
    input.onEvents?.(events);
  }
  return battle.result()!;
}

/**
 * Прогнать журнал волны. `start` берётся из журнала — сервер подставляет своё состояние забежки и свой сид
 * (`overrides`), как требует contracts/wave-log.
 */
export function replayWaveLog(
  log: WaveLog,
  balance: BalanceData,
  overrides: { run?: RunState; seed?: number; hooks?: BattleHooks; autoSkillsAllowed?: boolean } = {},
): WaveResult {
  return replayWave({
    balance,
    run: overrides.run ?? log.start,
    seed: overrides.seed ?? log.seed,
    commands: log.commands,
    hooks: overrides.hooks,
    ...(overrides.autoSkillsAllowed !== undefined ? { autoSkillsAllowed: overrides.autoSkillsAllowed } : {}),
  });
}

/** Поля итога, которые различаются (пусто — совпали). */
export function diffWaveResults(a: WaveResult, b: WaveResult): (keyof WaveResult)[] {
  const keys: (keyof WaveResult)[] = ["outcome", "ticks", "kills", "coins", "hqHp", "bossKilled", "stateHash"];
  return keys.filter((k) => a[k] !== b[k]);
}

/**
 * Повтор атаки по журналу: `run`, `seed` и `defense` подставляет сервер из записи атаки (как `overrides` у волны),
 * команды — из журнала клиента.
 */
export function replayAttackLog(
  log: Pick<AttackLog, "commands">,
  balance: BalanceData,
  setup: { run: RunState; seed: number; defense: AttackDefense; hooks?: BattleHooks; autoSkillsAllowed?: boolean },
): WaveResult {
  return replayWave({
    balance,
    run: setup.run,
    seed: setup.seed,
    commands: log.commands,
    attack: setup.defense,
    hooks: setup.hooks,
    ...(setup.autoSkillsAllowed !== undefined ? { autoSkillsAllowed: setup.autoSkillsAllowed } : {}),
  });
}
